import './Video23.scss'

import { Icon, ICONS } from '@bob/library'
import { AppProviderContext } from '@websrc/App/AppContext'
import glueFrame from '@websrc/App/Helpers/glueFrame'
import { matomo } from '@websrc/App/Tracking/Matomo'
import { CSLocales } from '@websrc/Typings/ContentStack'
import React, { FC, PropsWithChildren, useContext, useEffect, useRef, useState } from 'react'

export type Video23Type = {
  type: '23_video'
  photo_id: string
  token: string
  locale: CSLocales
  forcePause?: boolean
  fillVideo?: boolean
}

// eslint-disable-next-line max-lines-per-function
export const Video23: FC<PropsWithChildren<Video23Type>> = ({
  token,
  photo_id,
  locale,
  forcePause,
  fillVideo,
  children: placeholderImage,
}) => {
  const [isPlaying, setIsPlaying] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [wrapperRatio, setWrapperRatio] = useState<number | null>(null)
  const [videoRatio, setVideoRatio] = useState<number | null>(null)
  const [videoWidth, setVideoWidth] = useState<number | undefined>()
  const [videoHeight, setVideoHeight] = useState<number | undefined>()
  const [videoScale, setVideoScale] = useState(1)
  const { matomoPageTemplate } = useContext(AppProviderContext)

  const videoIframe = useRef<HTMLIFrameElement>(null)
  const videoWrapper = useRef<HTMLDivElement>(null)
  const videoPlayer = useRef<any>(null)

  const url = `//legoeducation.23video.com/v.ihtml/player.html?token=${token}&source=embed&photo%5fid=${photo_id}&autoPlay=0&autoMute=0&hideBigPlay=1&showDescriptions=0`

  const getVideoDimensions = () => {
    if (!videoPlayer.current) {
      return
    }

    // Only try to get dimensions if the player is actually ready and has video data
    try {
      if (videoRatio === null) {
        videoPlayer.current.get('video_aspect_ratio', (ratio: number) => {
          if (ratio && ratio > 0) setVideoRatio(ratio)
        })
      }
    } catch (_error) {
      // Silently catch - video data might not be loaded yet
    }

    try {
      if (videoHeight === null) {
        videoPlayer.current.get('video_frames_height', (height: number) => {
          if (height && height > 0) setVideoHeight(height)
        })
      }
    } catch (_error) {
      // Silently catch - video data might not be loaded yet
    }

    try {
      if (videoWidth === null) {
        videoPlayer.current.get('video_frames_width', (width: number) => {
          if (width && width > 0) setVideoWidth(width)
        })
      }
    } catch (_error) {
      // Silently catch - video data might not be loaded yet
    }

    if (wrapperRatio === null && videoWrapper.current) {
      setWrapperRatio(videoWrapper.current.offsetWidth / videoWrapper.current.offsetHeight)
    }
  }

  const initGlueFrame = () => {
    if (videoPlayer.current || !videoIframe.current) {
      return
    }

    videoPlayer.current = glueFrame(videoIframe.current, 'Player')

    if (videoPlayer.current !== null) {
      videoPlayer.current.get('ready', () => {
        // Wait a bit to ensure video metadata is loaded before getting dimensions
        setTimeout(() => {
          getVideoDimensions()
        }, 100)

        videoPlayer.current.bind('player:video:loadedmetadata', () => {
          // This event fires once when the video is started
          const splitLocale = locale.split('-')

          const video23Locale =
            locale === 'nb-no' ? 'no_NO' : `${splitLocale[0]}_${splitLocale[1].toUpperCase()}`

          videoPlayer.current.set('subtitleLocale', video23Locale)

          // Try getting dimensions again after metadata is loaded
          getVideoDimensions()
        })

        videoPlayer.current.bind(
          'player:video:play player:video:pause player:video:end',
          function (e: any) {
            getVideoDimensions()

            let playing = null

            switch (e) {
              case 'player:video:play':
                playing = true
                break
              case 'player:video:pause':
                playing = false
                break
              case 'player:video:end':
                playing = false
                break
            }

            if (playing !== null) {
              setIsPlaying(playing)
            }
          }
        )
      })
    }
  }

  useEffect(() => {
    if (videoRatio !== null && wrapperRatio !== null) {
      setVideoScale(videoRatio / wrapperRatio < 1 ? videoRatio / wrapperRatio : 1)
    }
  }, [videoRatio, wrapperRatio])

  useEffect(() => {
    if (videoPlayer.current) {
      videoPlayer.current.set('playing', !forcePause && isPlaying)
      setIsFullscreen(videoIframe.current === document.fullscreenElement)
    }
  }, [isPlaying, forcePause])

  useEffect(() => {
    initGlueFrame()
  })

  return (
    <div className={`video-23${fillVideo ? ' m--fill-video' : ''}`}>
      <button
        aria-label={'play'}
        className={`video-23__play-button${isPlaying ? ' playing' : ''}`}
        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
          matomo.trackEvent(
            'engagement',
            matomoPageTemplate,
            ['coral 23video url: ' + url],
            ['coral 23video button start'],
            'button'
          )
          // To make sure that if a video is shown inside a CampaignSpot, it should not follow the clickable container.
          e.stopPropagation()
          e.preventDefault()
          setIsPlaying(!isPlaying)
        }}
      >
        <Icon background={'#000'} color={'#FFF'} icon={ICONS.PLAY} size={80} />
      </button>
      <div className={`video-23__thumbnail-container${isPlaying ? ' hidden' : ' visible'}`}>
        {placeholderImage}
      </div>

      <div className={`video-23__wrapper${isPlaying ? ' playing' : ''}`} ref={videoWrapper}>
        <div
          className={'video-23__ratio-container'}
          style={{
            paddingBottom: `${100 / (videoRatio ? videoRatio : 1)}%`,
            transform: isPlaying ? `scale(${videoScale})` : 'scale(1)',
          }}
        >
          <iframe
            allow={'autoplay; fullscreen'}
            allowFullScreen={true}
            className={`video-23__iframe${isPlaying ? ' playing' : ''}${
              isFullscreen ? ' fullscreen' : ''
            }`}
            height={videoHeight}
            ref={videoIframe}
            src={url}
            title={'video'}
            width={videoWidth}
          />
        </div>
      </div>
    </div>
  )
}
